import { FC, useEffect, useState } from 'react';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Box,
	TextField,
	Autocomplete,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getAvailableEmsOptions, getPartners } from 'redux/reducers/referrals/selectors';
import { getEMerchant } from 'redux/reducers/monthlyFee/selectors';
import { TEMManagementRow } from 'components/EMManagement/types';
import { getEMerchantRequest } from 'redux/reducers/monthlyFee/reducer';
import {
	createReferralPartnersRequest,
	getAvailableEmsRequest,
	getPartnersRequest,
	createPartnerRequest,
	updatePartnerRequest,
} from 'redux/reducers/referrals/reducer';
import { IRequestWithPageSortFilterSearch } from 'redux/types';
import { TPartnersRow } from 'redux/reducers/referrals/types';

export const AddPartnerDialog: FC<{
	open: boolean;
	onClose: () => void;
	title: string;
	updatePageRequestData: IRequestWithPageSortFilterSearch;
	partnerToEdit: TPartnersRow | null;
}> = ({ open, onClose, title = 'Add Partner', updatePageRequestData, partnerToEdit }) => {
	const dispatch = useDispatch();

	const [partnerName, setPartnerName] = useState<string | null>(partnerToEdit?.partnerName ?? null);
	const [partnerError, setPartnerError] = useState<string | null>(null);
	const [partnersFee, setPartnersFee] = useState<string | null>(
		partnerToEdit?.partnersFee?.toString() ?? null,
	);
	const [partnersFeeError, setPartnersFeeError] = useState<string | null>(null);
	const [registrationAddress, setRegistrationAddress] = useState<string | null>(
		partnerToEdit?.registrationAddress ?? null,
	);
	const [bankAccountDetails, setBankAccountDetails] = useState<string | null>(
		partnerToEdit?.bankAccountDetails ?? null,
	);
	const [bankAccountDetailsError, setBankAccountDetailsError] = useState<string | null>(null);
	const [registrationAddressError, setRegistrationAddressError] = useState<string | null>(null);
	const validate = () => {
		let error = false;
		if (!partnerName) {
			setPartnerError('Partner is required');
			error = true;
		} else {
			setPartnerError(null);
		}
		if (!partnersFee) {
			setPartnersFeeError('Partners fee is required');
			error = true;
		} else if (Number.isNaN(Number(partnersFee))) {
			setPartnersFeeError('Partners fee must be a number');
			error = true;
		} else if (Number(partnersFee) < 0) {
			setPartnersFeeError('Partners fee must be greater than 0');
			error = true;
		} else {
			setPartnersFeeError(null);
		}
		if (!registrationAddress) {
			setRegistrationAddressError('Registration address is required');
			error = true;
		} else {
			setRegistrationAddressError(null);
		}
		if (!bankAccountDetails) {
			setBankAccountDetailsError('Bank account details are required');
			error = true;
		} else {
			setBankAccountDetailsError(null);
		}
		return error;
	};

	const reset = () => {
		setPartnerName(null);
		setPartnerError(null);
		setPartnersFee(null);
		setPartnersFeeError(null);
		setRegistrationAddress(null);
		setRegistrationAddressError(null);
		setBankAccountDetails(null);
		setBankAccountDetailsError(null);
	};

	const handleClose = () => {
		reset();
		onClose();
	};

	const handleChangePartnerName = (e: React.ChangeEvent<HTMLInputElement>) => {
		setPartnerName(e.target.value);
		if (e.target.value.length > 0) {
			setPartnerError(null);
		}
	};

	const handleChangePartnersFee = (e: React.ChangeEvent<HTMLInputElement>) => {
		setPartnersFee(e.target.value);
		if (e.target.value.length > 0) {
			setPartnersFeeError(null);
		}
	};

	const handleChangeRegistrationAddress = (e: React.ChangeEvent<HTMLInputElement>) => {
		setRegistrationAddress(e.target.value);
		if (e.target.value.length > 0) {
			setRegistrationAddressError(null);
		}
	};

	const handleChangeBankAccountDetails = (e: React.ChangeEvent<HTMLInputElement>) => {
		setBankAccountDetails(e.target.value);
		if (e.target.value.length > 0) {
			setBankAccountDetailsError(null);
		}
	};

	const handleSave = () => {
		const error = validate();
		if (error || !partnerName || !partnersFee || !registrationAddress || !bankAccountDetails) {
			return;
		}
		if (title === 'Edit Partner' && partnerToEdit) {
			dispatch(
				updatePartnerRequest({
					body: {
						id: partnerToEdit.id,
						partnerName,
						partnersFee: Number(partnersFee),
						registrationAddress,
						bankAccountDetails,
					},
					partnersResponse: updatePageRequestData,
				}),
			);
		} else {
			dispatch(
				createPartnerRequest({
					body: {
						partnerName,
						partnersFee: Number(partnersFee),
						registrationAddress,
						bankAccountDetails,
					},
					partnersResponse: updatePageRequestData,
				}),
			);
		}
		handleClose();
	};

	const partners = useSelector(getPartners);
	console.log('partners', partners);
	const partnersOptions = partners?.data?.map(partner => ({
		id: partner.id,
		name: partner.partnerName,
	}));
	const emsOptions = useSelector(getAvailableEmsOptions);

	useEffect(() => {
		if (!open) return;
		dispatch(getPartnersRequest({ pageNumber: 1, pageSize: 10000 }));
		dispatch(getAvailableEmsRequest());
	}, [dispatch, open]);

	useEffect(() => {
		if (!open) return;
		if (partnerToEdit) {
			setPartnerName(partnerToEdit.partnerName);
			setPartnersFee(partnerToEdit.partnersFee.toString());
			setRegistrationAddress(partnerToEdit.registrationAddress);
			setBankAccountDetails(partnerToEdit.bankAccountDetails);
		}
	}, [partnerToEdit, open]);

	return (
		<Dialog open={open} onClose={handleClose}>
			<DialogTitle>{title}</DialogTitle>
			<DialogContent>
				<Box
					sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '500px', marginTop: 2 }}
				>
					<TextField
						label="Partner Name"
						value={partnerName}
						onChange={handleChangePartnerName}
						error={!!partnerError}
						helperText={partnerError}
					/>
					<TextField
						label="Partners Fee"
						value={partnersFee}
						onChange={handleChangePartnersFee}
						error={!!partnersFeeError}
						helperText={partnersFeeError}
					/>
					<TextField
						label="Registration Address"
						value={registrationAddress}
						onChange={handleChangeRegistrationAddress}
						error={!!registrationAddressError}
						helperText={registrationAddressError}
					/>
					<TextField
						label="Bank Account Details"
						value={bankAccountDetails}
						onChange={handleChangeBankAccountDetails}
						error={!!bankAccountDetailsError}
						helperText={bankAccountDetailsError}
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose}>Cancel</Button>
				<Button onClick={handleSave} color="primary">
					{title === 'Add Partner' ? 'Add' : 'Edit'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
